import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Upload as KendoUpload } from "@progress/kendo-react-upload";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import Papa from "papaparse";

import ErrorBanner from "../../components/ErrorBanner";
import { useQueryGetClientsMasterList } from "../../data/services/clientAdminService";
import { useMutatePostReportExclusionClients } from "../../data/services/reportExclusionService";

const ACTIONS = {
  EXCLUDE: "Exclude",
  ALREADY_EXCLUDED: "Already Excluded",
  NOT_NEEDED: "Not Needed",
  INVALID: "Invalid",
};

// Numeric-only IDs are zero-padded to 5 chars to match the workflow-wide
// convention (see pages/workflow/Panels/OEWorkflowImportClientsModel.jsx), so a
// file containing 2500 matches the master-list 02500.
const normalizeId = (raw) => {
  if (raw === undefined || raw === null) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  return /^\d+$/.test(trimmed) ? trimmed.padStart(5, "0") : trimmed;
};

// Mirrors ReportExclusionFilter.IsDemoClient on the server: a client id that
// reads as a number below 1000. Zero padding is why this parses rather than
// compares strings.
export const isDemoClientId = (clientId) => {
  const trimmed = String(clientId ?? "").trim();
  return /^\d+$/.test(trimmed) && Number(trimmed) < 1000;
};

/**
 * CSV import for the report exclusion list.
 *
 * Append/upsert only: clients already excluded are left unchanged, and clients
 * absent from the file are never restored. Restoring is an explicit action on
 * the grid.
 *
 * Demo clients are previewed as "Not Needed" and left out of the payload. The
 * server would report them back as demoClientCount and store nothing anyway,
 * but saying so before Apply beats a silent no-op afterwards.
 */
const ReportExclusionsImportModal = ({
  oePeriodId,
  currentClientIds,
  onClose,
  onSubmit,
}) => {
  const [files, setFiles] = React.useState([]);
  const [parseError, setParseError] = React.useState(null);
  const [fileRows, setFileRows] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);

  const { data: availableClients } = useQueryGetClientsMasterList();
  const addClientsHandler = useMutatePostReportExclusionClients();

  const currentIds = React.useMemo(
    () => new Set((currentClientIds || []).map(normalizeId)),
    [currentClientIds]
  );

  const validClientsById = React.useMemo(() => {
    const map = new Map();
    (availableClients?.data || []).forEach((client) => {
      map.set(normalizeId(client.clientId), client);
    });
    return map;
  }, [availableClients]);

  const previewRows = React.useMemo(() => {
    const seen = new Set();
    const rows = [];

    for (const id of fileRows) {
      if (!id || seen.has(id)) continue;
      seen.add(id);

      const validClient = validClientsById.get(id);

      // Checked before the master-list lookup: a demo client that is missing
      // from the master list is still "already excluded by rule", and calling
      // it Invalid would send the admin chasing a client id that does not
      // matter either way.
      if (isDemoClientId(id)) {
        rows.push({
          clientId: id,
          clientName: validClient ? validClient.clientName : "(demo client)",
          action: ACTIONS.NOT_NEEDED,
        });
      } else if (!validClient) {
        rows.push({
          clientId: id,
          clientName: "(unknown)",
          action: ACTIONS.INVALID,
          invalidReason: "Client not in master list",
        });
      } else if (currentIds.has(id)) {
        rows.push({
          clientId: validClient.clientId,
          clientName: validClient.clientName,
          action: ACTIONS.ALREADY_EXCLUDED,
        });
      } else {
        rows.push({
          clientId: validClient.clientId,
          clientName: validClient.clientName,
          action: ACTIONS.EXCLUDE,
        });
      }
    }

    return rows;
  }, [fileRows, validClientsById, currentIds]);

  const summary = React.useMemo(
    () => ({
      exclude: previewRows.filter((row) => row.action === ACTIONS.EXCLUDE),
      alreadyExcluded: previewRows.filter(
        (row) => row.action === ACTIONS.ALREADY_EXCLUDED
      ),
      notNeeded: previewRows.filter((row) => row.action === ACTIONS.NOT_NEEDED),
      invalid: previewRows.filter((row) => row.action === ACTIONS.INVALID),
    }),
    [previewRows]
  );

  const canApply = !submitting && !parseError && summary.exclude.length > 0;

  const onAddFileHandler = (event) => {
    setFiles(event.newState);
    setParseError(null);
    setFileRows([]);

    const rawFile = event.newState[0]?.getRawFile?.();
    if (!rawFile) return;

    Papa.parse(rawFile, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => (header || "").trim(),
      complete: (results) => {
        const headers = results.meta?.fields || [];
        const clientIdHeader = headers.find(
          (header) => header.toLowerCase() === "clientid"
        );

        if (!clientIdHeader) {
          setParseError(
            "The file is missing a required 'ClientId' column header."
          );
          setFileRows([]);
          return;
        }

        const parsed = results.data
          .map((row) => normalizeId(row[clientIdHeader]))
          .filter((clientId) => clientId.length > 0);

        if (parsed.length === 0) {
          setParseError("The file does not contain any Client Ids.");
          setFileRows([]);
          return;
        }

        setFileRows(parsed);
      },
      error: () => {
        setParseError("Failed to parse the file. Please check the format.");
        setFileRows([]);
      },
    });
  };

  const onRemoveFileHandler = (event) => {
    setFiles(event.newState);
    setFileRows([]);
    setParseError(null);
  };

  const onDownloadTemplate = () => {
    const csvData = Papa.unparse([{ ClientId: "" }]);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Report Exclusions Import Template.csv";
    link.click();
    // The object URL pins the blob in memory for the life of the document.
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      await addClientsHandler.mutateAsync({
        oePeriodId,
        clientIds: summary.exclude.map((row) => row.clientId),
      });
      setSubmitting(false);
      onSubmit();
    } catch {
      setSubmitting(false);
    }
  };

  const actionCell = (props) => {
    const action = props.dataItem.action;
    const color =
      action === ACTIONS.EXCLUDE
        ? "green"
        : action === ACTIONS.INVALID
        ? "#a94442"
        : "";
    return (
      <td
        style={{
          color,
          fontWeight: action === ACTIONS.INVALID ? "bold" : "",
        }}
        title={props.dataItem.invalidReason ?? ""}
      >
        {action}
      </td>
    );
  };

  return (
    <Dialog
      title={"Import clients to exclude"}
      onClose={onClose}
      width={700}
    >
      <ErrorBanner handler={addClientsHandler} />
      <p>
        Upload a CSV with a <b>ClientId</b> column. Clients on the file are
        excluded from the dashboard charts and the status tracking report for
        the selected OE period.
      </p>
      <p>
        Clients already excluded are left unchanged, and clients <b>not</b> on
        the file are never restored.
      </p>
      <p>
        <Button themeColor={"info"} fillMode="flat" onClick={onDownloadTemplate}>
          Download Template
        </Button>
      </p>
      <KendoUpload
        autoUpload={false}
        showActionButtons={false}
        files={files}
        multiple={false}
        onAdd={onAddFileHandler}
        onRemove={onRemoveFileHandler}
        restrictions={{ allowedExtensions: [".csv"] }}
      />
      {parseError && (
        <div
          style={{
            backgroundColor: "#f8d7da",
            padding: 10,
            marginTop: 10,
            borderRadius: ".25rem",
            color: "#721c24",
          }}
        >
          {parseError}
        </div>
      )}
      {previewRows.length > 0 && (
        <>
          <div style={{ marginTop: 20 }}>
            <b>{summary.exclude.length}</b> to exclude,{" "}
            <b>{summary.alreadyExcluded.length}</b> already excluded,{" "}
            <b>{summary.notNeeded.length}</b> not needed,{" "}
            <b>{summary.invalid.length}</b> invalid.
          </div>
          {summary.notNeeded.length > 0 && (
            <div
              data-testid="demo-client-warning"
              style={{
                backgroundColor: "#fff3cd",
                padding: 10,
                marginTop: 10,
                borderRadius: ".25rem",
                color: "#856404",
              }}
            >
              {summary.notNeeded.length} row(s) are demo clients (client id
              below 1000). They are already left out of every report, so they
              are skipped rather than added to the list.
            </div>
          )}
          {summary.invalid.length > 0 && (
            <div
              style={{
                backgroundColor: "#fff3cd",
                padding: 10,
                marginTop: 10,
                borderRadius: ".25rem",
                color: "#856404",
              }}
            >
              {summary.invalid.length} row(s) are invalid and will be skipped
              (client id not in the master list). Hover a row&apos;s Action cell
              for the reason.
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <Grid data={previewRows} style={{ maxHeight: "300px" }}>
              <Column field="clientId" title="Client Id" width={120} />
              <Column field="clientName" title="Client Name" />
              <Column
                field="action"
                title="Action"
                width={180}
                cell={actionCell}
              />
            </Grid>
          </div>
        </>
      )}
      <div style={{ paddingTop: 20 }}></div>
      <DialogActionsBar layout={"end"}>
        <Button onClick={onClose}>{"Cancel"}</Button>
        <Button
          type={"submit"}
          themeColor={"success"}
          disabled={!canApply}
          onClick={handleSubmit}
        >
          {"Apply"}
          {submitting && <span className="k-icon k-i-loading"></span>}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default ReportExclusionsImportModal;
